import { designVariables } from "@studio3t/design-variables";
import {
  ButtonSize,
  ButtonSmallSecondaryIcon,
  GenericAlert,
  GenericButton,
  Icon,
} from "@studio3t/generic-components";
import type React from "react";
import { useId } from "react";
import styled from "styled-components";
import type { SelectOption } from "./SelectDropdown";
import { SelectDropdown } from "./SelectDropdown";
import { StepperInput } from "./StepperInput";
import {
  FieldRow,
  FieldRowControl,
  FieldRowEven,
  FormGroup,
  FormSection,
  HelpText,
  Input,
  InsetPanelRow,
  Label,
  ListAddRow,
  SectionTitle,
  Subsection,
  SubsectionText,
  SubsectionTitle,
} from "./styles";

const BUTTON = designVariables.ui.button;
const FORM = designVariables.ui.form;
const SEMANTIC = designVariables.ui.semantic;

export interface FilterRow {
  /// Identity rather than position, so removing a row does not move the row
  /// below it onto the removed one's input state.
  id: number;
  field: string;
  value: string;
}

export interface ScannerFieldsProps {
  targetService: string;
  setTargetService: (value: string) => void;
  targetOptions: SelectOption<string>[];
  targetsLoading: boolean;
  orderBy: string;
  setOrderBy: (value: string) => void;
  from: string;
  setFrom: (value: string) => void;
  to: string;
  setTo: (value: string) => void;
  pageSize: string;
  setPageSize: (value: string) => void;
  filters: FilterRow[];
  setFilter: (id: number, row: Partial<FilterRow>) => void;
  addFilter: () => void;
  removeFilter: (id: number) => void;
}

/**
 * Configuration for a scanner service — how to traverse data that is already
 * there — laid out per Figma 16097-33436 / 16099-154777: the target row,
 * ordering key and page size side by side, the bounds in an inset panel,
 * then the filter list under its own heading.
 *
 * Two things about a scanner surprise people, so both are said here rather
 * than left to be discovered: only MongoDB and PostgreSQL can be scanned,
 * which is why the target list is short; and `to` alone decides whether the
 * scan ever ends, which is why the bounds panel reports what the current
 * setting means instead of offering a mode to pick.
 */
export function ScannerFields(props: ScannerFieldsProps) {
  return (
    <FormSection>
      <SectionTitle>Scanner configuration</SectionTitle>
      <TargetField {...props} />
      <PagingFields {...props} />
      <BoundsPanel {...props} />
      <FilterFields {...props} />
    </FormSection>
  );
}

function TargetField({
  targetService,
  setTargetService,
  targetOptions,
  targetsLoading,
}: ScannerFieldsProps) {
  const noTargets = !targetsLoading && targetOptions.length === 0;

  return (
    <FieldRow>
      <Label as="span">Target service*</Label>
      <FieldRowControl>
        <SelectDropdown
          value={targetService}
          options={targetOptions}
          onChange={setTargetService}
          onClear={() => setTargetService("")}
          disabled={targetsLoading || noTargets}
          placeholder="Select a service"
        />
        <HelpText>
          {noTargets
            ? "No MongoDB or PostgreSQL services yet, add one first. A scan needs an ordered, unique key to page through, which Kafka and Pub/Sub do not offer."
            : "The service holding the data to read. Its credentials stay with it, a scanner never carries its own. Only MongoDB and PostgreSQL services appear here."}
        </HelpText>
      </FieldRowControl>
    </FieldRow>
  );
}

/** Ordering key and page size share one row (Figma 16099-154416). */
function PagingFields({
  orderBy,
  setOrderBy,
  pageSize,
  setPageSize,
}: ScannerFieldsProps) {
  const orderById = useId();
  const pageSizeId = useId();

  return (
    <FieldRowEven>
      <FormGroup>
        <Label htmlFor={orderById}>Order by</Label>
        <Input
          id={orderById}
          type="text"
          value={orderBy}
          onChange={(e) => setOrderBy(e.target.value)}
          placeholder="_id"
        />
        <HelpText>
          Field to page through (default: _id, or a table's primary key). Must
          be indexed to avoid slow full scans.
        </HelpText>
      </FormGroup>

      <FormGroup>
        <Label htmlFor={pageSizeId}>Page size</Label>
        <StepperInput
          id={pageSizeId}
          label="page size"
          value={pageSize}
          onChange={setPageSize}
          placeholder="500"
        />
        <HelpText>
          Documents read per page, which is also the pipeline&apos;s batch size.
          Defaults to 500.
        </HelpText>
      </FormGroup>
    </FieldRowEven>
  );
}

/**
 * The bounds, in their own inset panel (Figma 16099-154176): From keeps a
 * fixed width, To takes the rest and carries the alert that says what the
 * current upper bound means.
 */
function BoundsPanel({ from, setFrom, to, setTo }: ScannerFieldsProps) {
  const fromId = useId();
  const toId = useId();

  return (
    <InsetPanelRow>
      <FromGroup>
        <Label htmlFor={fromId}>From</Label>
        <Input
          id={fromId}
          type="text"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <HelpText>
          Leave empty to start at the beginning. Exclusive lower bound on the
          ordering field, read as a number if numeric, otherwise as text.
        </HelpText>
      </FromGroup>

      <ToColumn>
        <FormGroup>
          <Label htmlFor={toId}>To</Label>
          <Input
            id={toId}
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </FormGroup>
        <MeaningAlert
          state="Info"
          icon
          dismissible={false}
          width="100%"
          message={<UpperBoundMeaning bounded={to.trim() !== ""} />}
        />
      </ToColumn>
    </InsetPanelRow>
  );
}

/**
 * What the current `to` setting means, said at the moment it is decided.
 *
 * The engine has no mode to choose — an absent upper bound *is* the tailing
 * setting — so the form reports the consequence rather than inventing a
 * toggle to sit beside it.
 */
function UpperBoundMeaning({ bounded }: { bounded: boolean }) {
  if (bounded) {
    return (
      <>
        <strong>This scan finishes.</strong> It stops on reaching this inclusive
        upper bound, and the pipeline ends with it. Must be the same kind of
        value as From.
      </>
    );
  }
  return (
    <>
      <strong>This scan will not finish.</strong> Leave empty to scan
      continuously, picking up new inserts after existing documents. Ideal for
      append-only collections without a change stream.
    </>
  );
}

// One filter row (Figma 16099-154483): Field and Value as inline label +
// input pairs, and the design-system minus button while the row is not the
// only one.
function FilterRowFields({
  row,
  index,
  isLast,
  inputId,
  removable,
  setFilter,
  removeFilter,
  onKeyDown,
}: {
  row: FilterRow;
  index: number;
  isLast: boolean;
  inputId: string;
  removable: boolean;
  setFilter: (id: number, patch: Partial<FilterRow>) => void;
  removeFilter: (id: number) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}) {
  const fieldId = `${inputId}-field-${row.id}`;
  const valueId = `${inputId}-value-${row.id}`;
  return (
    <FilterRowLine $removable={removable}>
      <FieldRow>
        <Label htmlFor={fieldId}>Field</Label>
        <FieldRowControl>
          <Input
            id={fieldId}
            type="text"
            value={row.field}
            onChange={(e) => setFilter(row.id, { field: e.target.value })}
            onKeyDown={isLast ? onKeyDown : undefined}
          />
        </FieldRowControl>
      </FieldRow>
      <FieldRow>
        <Label htmlFor={valueId}>Value</Label>
        <FieldRowControl>
          <Input
            id={valueId}
            type="text"
            value={row.value}
            onChange={(e) => setFilter(row.id, { value: e.target.value })}
            onKeyDown={isLast ? onKeyDown : undefined}
          />
        </FieldRowControl>
      </FieldRow>
      {removable && (
        <ButtonSmallSecondaryIcon
          // GenericButton derivatives drop type/aria props; both are
          // set on the real <button> via ref (type defaults to
          // "submit" inside the form).
          ref={(el) => {
            el?.setAttribute("type", "button");
            el?.setAttribute("aria-label", `Remove filter ${index + 1}`);
          }}
          icon={Icon.Minus}
          onClick={() => removeFilter(row.id)}
        />
      )}
    </FilterRowLine>
  );
}

// The filter heading has no rule (Figma 16099-154557); the rows follow a
// form group below it.
function FilterFields({
  filters,
  setFilter,
  addFilter,
  removeFilter,
}: ScannerFieldsProps) {
  const inputId = useId();
  const last = filters[filters.length - 1];
  const rowComplete =
    !last || (last.field.trim() !== "" && last.value.trim() !== "");

  // Enter in a completed row adds the next one, as in the HTTP Source
  // routes list; the Add button needs no such gate, since submit drops
  // half-typed rows.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (rowComplete) addFilter();
    }
  };

  return (
    <>
      <Subsection>
        <SubsectionTitle>Filter</SubsectionTitle>
        <SubsectionText>
          Applied before ordering. Each row matches a field to a value exactly,
          there are no operators, and the engine refuses one that reaches it
          another way. Use From and To for a range.
        </SubsectionText>
      </Subsection>
      <FilterList>
        {filters.map((row, index) => (
          <FilterRowFields
            key={row.id}
            row={row}
            index={index}
            isLast={index === filters.length - 1}
            inputId={inputId}
            removable={filters.length > 1}
            setFilter={setFilter}
            removeFilter={removeFilter}
            onKeyDown={handleKeyDown}
          />
        ))}
        <ListAddRow>
          <GenericButton
            // GenericButton's `type` prop is the variant; the HTML type
            // attribute is set via ref.
            ref={(el) => el?.setAttribute("type", "button")}
            type="secondary"
            size={ButtonSize.Medium}
            icon={Icon.Plus}
            iconPosition="leading"
            text="Add"
            onClick={addFilter}
          />
        </ListAddRow>
      </FilterList>
    </>
  );
}

// The library centres the alert icon against its text; the mock keeps it on
// the first line of this multi-line message (Figma 16099-154630). The icon
// and text share the alert's inner row.
const MeaningAlert = styled(GenericAlert)`
  > div > div:first-child {
    align-items: flex-start;
  }
`;

// From keeps the Figma width (16099-154303); To fills the rest.
const FROM_WIDTH = 240;

const FromGroup = styled(FormGroup)`
  flex: 0 0 ${FROM_WIDTH}px;
`;

// To input with its meaning alert a spacingXS below (Figma 16099-154763).
const ToColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SEMANTIC.spacingxs}px;
`;

const FilterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${FORM.gapitems}px;
  width: 100%;

  > ${ListAddRow} {
    padding-top: 0;
  }
`;

// Field and Value split the row evenly; the minus button gets a slot of its
// own only while the rows show one, so a lone row spans the full width.
const FilterRowLine = styled.div<{ $removable: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr)${(props) =>
    props.$removable ? ` ${BUTTON.s.size}px` : ""};
  align-items: start;
  gap: ${FORM.gapitems}px;
`;
